import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Typography, Tooltip } from '@mui/material';
import LocalHospital from '@mui/icons-material/LocalHospital';
import { useUnidade, Unidade } from '../contexts/UnidadeContext';
import { useAuth } from '../contexts/AuthContext';
import { abreviarUnidadeNome, sufixoComumUnidades } from '../utils/unidade-nome';

/**
 * Seletor "Unidade de Saude" — renderizado no menu lateral, logo abaixo do
 * seletor de Municipio. Default vazio (= Todas as unidades). Quando uma unidade
 * e selecionada, as telas do /saude filtram por ela (via UnidadeContext).
 * Aparece para TODOS os municipios com municipio ativo, mesmo sem unidades
 * cadastradas na tabela (lista vazia => apenas "Todas as unidades").
 *
 * Como a caixa tem pouca largura, o campo fechado exibe o nome REDUZIDO
 * (ver utils/unidade-nome); o nome completo aparece no tooltip e ao abrir a lista.
 */
const UnidadeSaudeSelector: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const { unidades, unidadeSelecionada, setUnidadeSelecionada, loading } = useUnidade();
  const [aberto, setAberto] = useState(false);
  // null = usuario nao digitou nada desde que abriu a lista
  const [digitado, setDigitado] = useState<string | null>(null);

  const sufixo = useMemo(
    () => sufixoComumUnidades(unidades.map((u) => u.nome), municipioAtivo?.nome),
    [unidades, municipioAtivo],
  );

  const nomeCompleto = unidadeSelecionada?.nome || '';
  const nomeCurto = useMemo(
    () => (nomeCompleto ? abreviarUnidadeNome(nomeCompleto, sufixo) : ''),
    [nomeCompleto, sufixo],
  );

  // So depende de haver um municipio ativo (nao do numero de unidades)
  if (!municipioAtivo) return null;

  // Fechado: nome reduzido. Aberto: nome completo (ou o texto digitado).
  // Quando aberto sem digitacao o inputValue e exatamente o label da opcao
  // selecionada, e o MUI mostra a lista completa nesse caso.
  const inputValue = aberto ? (digitado ?? nomeCompleto) : nomeCurto;
  const tooltip = !aberto && nomeCompleto && nomeCompleto !== nomeCurto ? nomeCompleto : '';

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <Box sx={{ mx: 1.5, mb: 0.5, px: 1.5, py: 1, borderRadius: '8px', backgroundColor: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <LocalHospital sx={{ fontSize: 14, color: '#34d399' }} />
          <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', lineHeight: 1.2 }}>
            Unidade de Saude
          </Typography>
        </Box>
        <Autocomplete
          size="small"
          options={unidades}
          value={unidadeSelecionada}
          loading={loading}
          open={aberto}
          onOpen={() => setAberto(true)}
          onClose={() => { setAberto(false); setDigitado(null); }}
          inputValue={inputValue}
          onInputChange={(_e, valor, motivo) => { if (motivo === 'input') setDigitado(valor); }}
          onChange={(_e, value: Unidade | null) => setUnidadeSelecionada(value)}
          getOptionLabel={(o) => o?.nome || ''}
          isOptionEqualToValue={(o, v) => o.codunidade === v.codunidade}
          noOptionsText="Nenhuma unidade"
          loadingText="Carregando..."
          clearText="Todas as unidades"
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Todas as unidades"
              variant="standard"
              InputProps={{ ...params.InputProps, disableUnderline: true }}
            />
          )}
          slotProps={{
            // Lista mais larga que a barra lateral, para caber o nome completo
            popper: { style: { width: 'auto' }, sx: { minWidth: 300, maxWidth: 420 } },
            paper: {
              sx: {
                '& .MuiAutocomplete-option': { fontSize: '0.8125rem', whiteSpace: 'normal', lineHeight: 1.35 },
              },
            },
          }}
          sx={{
            // pr reservado para os icones de limpar (X) e abrir (v) — sem isso o
            // nome da unidade passa por baixo deles
            '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap', pr: '56px !important' },
            '& .MuiInputBase-root': { fontSize: '0.75rem', color: 'white', py: '0 !important', pl: '0 !important' },
            '& .MuiInputBase-input': {
              color: 'white',
              p: '2px 0 !important',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .MuiInputBase-input::placeholder': { color: '#cbd5e1', opacity: 1 },
            '& .MuiSvgIcon-root': { color: '#94a3b8' },
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default UnidadeSaudeSelector;
